import { FC, useState } from 'react';

import {
  Text,
  Flex,
  Button,
  Box,
  Input,
  Divider,
  Tooltip,
} from '@chakra-ui/react';
import { Report, InventoryReportStatusEnum } from 'types/report';
import { UpdateStatusProp } from './EditHarvestSummaryModal.props';
import { InventoryReportTypeEnum } from 'types/inventory';

import { Link } from 'react-router-dom';

import { formatCurrency, formatNumber } from 'utils/formatNumbers';
import {
  FeeAdjustmentRow,
  sumFeeAdjustments,
  toFeeRows,
} from 'constants/feeAdjustments';
import FeeAdjustmentsEditor from 'components/primitives/FeeAdjustmentsEditor';
import FormikTextArea from 'components/primitives/FormikTextArea';
import { useFormikContext } from 'formik';
import { calculateLost } from 'utils/calculateLost';
import { calculateInvoicingValues } from './utils';
import { routesSettings } from 'constants/routes';

type InvoicingTabProps = {
  report: Report;
  updateStatus: UpdateStatusProp;
  isLoading: boolean;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) => void;
};

const InvoicingTab: FC<InvoicingTabProps> = ({
  report,
  updateStatus,
  isLoading,
  setFieldValue,
}) => {
  const details = report.details;
  const [cokeAdj, setCokeAdj] = useState<number>(report.cokeAdjustment || 0);
  // Read-only fallback shown until Formik hydrates the field value.
  const invoiceNotes = report.invoiceNotes || report.site.invoiceNotes || '';
  const { values: formikValues } = useFormikContext<any>();

  const [fees, setFees] = useState<FeeAdjustmentRow[]>(
    toFeeRows(report.feeAdjustments, report.feeAdjustment),
  );
  const totalFees = sumFeeAdjustments(fees);

  const onFeesChange = (rows: FeeAdjustmentRow[]) => {
    setFees(rows);
    // Keep Formik in sync so the modal-wide Save (PATCH /report/edit/:id)
    // persists the rows too, not just Mark As Invoiced.
    setFieldValue('feeAdjustments', rows);
    setFieldValue('feeAdjustment', sumFeeAdjustments(rows));
  };

  const handleWheel = (event: { preventDefault: () => void }) => {
    event.preventDefault();
  };

  const preventMouseScroll = (event: {
    target: {
      addEventListener: (
        arg0: string,
        arg1: (event: { preventDefault: () => void }) => void,
        arg2: { passive: boolean },
      ) => void;
    };
  }) => {
    event.target.addEventListener('wheel', handleWheel, { passive: false });
  };

  const values = calculateInvoicingValues(
    details,
    report?.site,
    report?.reportType,
  );

  // Invoice Notes and Billing Details are read-only here; they're maintained on
  // the customer's site page. Link there (new tab) so editors can jump across.
  const editInCustomerDetailsLink = report?.site?._id ? (
    <Link
      to={routesSettings.PORTAL_SITE_DETAILS.getPath(report.site._id)}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: '#2FB64A', fontWeight: 600 }}>
      Edit in customer details
    </Link>
  ) : undefined;

  const markAsInvoiced = () => {
    const reportId = report._id;
    updateStatus({
      reportId,
      status: InventoryReportStatusEnum.DONE,
      cokeAdjustment: cokeAdj,
      feeAdjustment: totalFees,
      feeAdjustments: fees,
      invoiceNotes: formikValues.invoiceNotes,
      notes: formikValues.notes,
    });
  };

  const unMarkAsInvoiced = () => {
    const reportId = report._id;
    updateStatus({
      cokeAdjustment: cokeAdj,
      feeAdjustment: totalFees,
      feeAdjustments: fees,
      reportId,
      status: InventoryReportStatusEnum.READY_FOR_INVOICE,
    });
  };
  return (
    <Flex flex={1} direction="column" mb="20px">
      <Flex
        flex={1}
        direction="column"
        fontSize="14px"
        textAlign="center"
        fontWeight="400">
        <Flex flex={1} direction="row">
          <Box>
            <Text
              flex={1.5}
              minW="120px"
              p="15px"
              bg="surface.400"
              fontWeight={600}>
              Items
            </Text>

            {details.map((item, index) => {
              return (
                <Flex
                  key={`${item.sku._id}-${index}`}
                  flex={1}
                  minH="52px"
                  maxW={96}
                  direction="row"
                  p={item.sku.name.length > 16 ? 2 : '15.5px'}
                  bg={index % 2 === 0 ? 'surface.200' : 'surface.100'}
                  borderBottom={
                    index % 2 === 0 ? 'surface.200' : 'surface.100'
                  }>
                  <Tooltip label={item.sku.name} hasArrow placement="top-start">
                    <Text
                      flex={1.5}
                      noOfLines={item.sku.name.length > 16 ? 2 : 1}
                      fontSize={item.sku.name.length > 16 ? '12px' : '14px'}
                      textOverflow="ellipsis">
                      {item.sku.name}
                    </Text>
                  </Tooltip>
                </Flex>
              );
            })}
            <Flex>
              <Text
                bg="#B5E3BA"
                flex={1.5}
                minW="120px"
                p="15px"
                fontWeight="bold">
                Total Items
              </Text>
            </Flex>
          </Box>

          <Box flexGrow={1} position="relative">
            <Flex
              flex={1}
              direction="row"
              bg="surface.400"
              p="15px"
              fontWeight="semibold">
              <Text flex={0.5} minW="120px">
                Lost
              </Text>
              <Text flex={0.5} minW="120px">
                Replacement Fee
              </Text>
              <Text flex={0.5} minW="120px">
                Total Lost Fees
              </Text>
            </Flex>
            {details.map((item, index) => {
              const sitePricingDetails =
                report?.site?.pricingDetails?.filter(
                  (pd) => pd.itemSku === item.sku._id,
                ) || [];
              const siteReplacementPrice =
                sitePricingDetails.length > 0
                  ? sitePricingDetails[0].replacementPrice
                  : item?.sku?.pricePerReplacement;
              const lost = calculateLost(item);

              return (
                <Flex
                  key={`${item.sku._id}-${index}`}
                  flex={1}
                  direction="row"
                  bg={index % 2 === 0 ? 'surface.200' : 'surface.100'}>
                  <Text
                    flex={0.5}
                    p="15px"
                    borderBottom={
                      index % 2 === 0 ? 'surface.200' : 'surface.100'
                    }>
                    {formatNumber(lost)}
                  </Text>
                  <Text flex={0.5} p="15px">
                    {formatCurrency(Number(siteReplacementPrice)) || '-'}
                  </Text>
                  <Text
                    flex={0.5}
                    bg="#FCEDED"
                    p="15px"
                    borderBottom="1px solid #E6CFCF">
                    {formatCurrency(lost * (Number(siteReplacementPrice) || 0))}
                  </Text>
                </Flex>
              );
            })}
            <Flex bg="#B5E3BA" flexGrow={1} zIndex="100" fontWeight="bold">
              <Text flex={1} p="15px" minW="130px">
                {formatNumber(values.lost)}
              </Text>
              <Text flex={1} p="15px" minW="130px">
                -
              </Text>
              <Text
                flex={1}
                p="15px"
                minW="130px"
                borderBottomRightRadius="10px"
                bg="#96CB9B">
                {formatCurrency(values.totalLostFees)}
              </Text>
            </Flex>
            <Box
              borderRadius="10px"
              border="1px solid #2FB64A"
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              zIndex={100}
              flexGrow={1}></Box>
          </Box>

          {/* used */}
          {report.reportType === InventoryReportTypeEnum.NO_INVENTORY && (
            <Box position="relative" flex="2">
              <Flex
                flex={1}
                direction="row"
                bg="surface.400"
                p="15px"
                borderTopRightRadius={10}
                fontWeight="semibold">
                <Text flex={1} minW="120px">
                  Used
                </Text>
                <Text flex={1} minW="120px">
                  Used Fee
                </Text>
                <Text flex={1} minW="120px">
                  Total Used Fees
                </Text>
              </Flex>
              {details.map((item, index) => {
                const sitePricingDetails =
                  report?.site?.pricingDetails?.filter(
                    (pd) => pd.itemSku === item.sku._id,
                  ) || [];
                const siteDeliveryPrice =
                  sitePricingDetails.length > 0
                    ? sitePricingDetails[0].deliveryPrice
                    : item.sku.pricePerDelivery;

                const lost = calculateLost(item);
                return (
                  <Flex
                    key={`${item.sku._id}`}
                    flex={1}
                    direction="row"
                    bg={index % 2 === 0 ? 'surface.200' : 'surface.100'}>
                    <Text
                      flex={1}
                      p="15px"
                      borderBottom={
                        index % 2 === 0 ? 'surface.200' : 'surface.100'
                      }>
                      {formatNumber(item.ret + lost)}
                    </Text>
                    <Text flex={1} p="15px">
                      {formatCurrency(Number(siteDeliveryPrice)) || '-'}
                    </Text>
                    <Text
                      flex={1}
                      bg="#EDF5FC"
                      p="15px"
                      borderBottom="1px solid #CFE1E6">
                      {formatCurrency(
                        (item.ret + lost) * (siteDeliveryPrice || 0),
                      )}
                    </Text>
                  </Flex>
                );
              })}
              <Flex bg="#B5E3BA" borderBottomRightRadius={10} fontWeight="bold">
                <Text flex={1} p="15px" minW="130px">
                  {formatNumber(values.used)}
                </Text>
                <Text flex={1} p="15px" minW="130px">
                  -{' '}
                </Text>
                <Text
                  borderBottomRightRadius={10}
                  flex={1}
                  p="15px"
                  minW="130px"
                  bg="#96CB9B">
                  {formatCurrency(values.totalUsedFees)}
                </Text>
              </Flex>
              <Box
                ml={2}
                borderRadius="10px"
                border="1px solid #2FB64A"
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex={100}
                flexGrow={1}></Box>
            </Box>
          )}
        </Flex>
      </Flex>
      {
        <Flex direction="column" justifyContent="space-between">
          <Flex mt="20px" direction="row" justifyContent="space-between">
            <Flex w="60%" mr={5}>
              <FormikTextArea
                name="notes"
                label="Harvest Summary Notes"
                value={formikValues.notes || report.notes}
                height="90%"
              />
            </Flex>

            <Flex flexDir="column" gap={5}>
              <Flex gap={5} flexDir="column" maxW="23rem">
                <Flex
                  color="black"
                  flexDir="row"
                  justifyContent="space-between"
                  fontWeight="medium">
                  <Text>Subtotal</Text>
                  <Text>{formatCurrency(values.subtotal)}</Text>
                </Flex>

                {report.site.hasCokeAdjustment && (
                  <Flex
                    color="black"
                    flexDir="row"
                    alignItems="center"
                    justifyContent="space-between"
                    fontWeight="medium">
                    <Text>Coke Adjustments</Text>
                    <Text ml="4">
                      -$
                      <Input
                        onFocus={preventMouseScroll}
                        marginLeft={1}
                        maxW="8rem"
                        type="number"
                        value={cokeAdj ? cokeAdj : ''}
                        onChange={(input) => {
                          setCokeAdj(
                            parseFloat(input.currentTarget.value) || 0,
                          );
                          setFieldValue(
                            'cokeAdjustment',
                            input.currentTarget.value,
                          );
                        }}
                      />
                    </Text>
                  </Flex>
                )}
                <FeeAdjustmentsEditor rows={fees} onChange={onFeesChange} />
                <Divider />
                <Flex
                  color="black"
                  flexDir="row"
                  justifyContent="space-between"
                  fontWeight="medium">
                  <Text>Total</Text>
                  <Text>
                    {formatCurrency(values.subtotal - cokeAdj + totalFees)}
                  </Text>
                </Flex>
              </Flex>
              {report.status ===
                InventoryReportStatusEnum.READY_FOR_INVOICE && (
                <Button
                  color={'white'}
                  variant="fill"
                  marginRight="10px"
                  onClick={markAsInvoiced}
                  isDisabled={isLoading}
                  maxW="23rem">
                  Mark As Invoiced
                </Button>
              )}
              {report.status === InventoryReportStatusEnum.DONE && (
                <Button
                  color="white"
                  variant="fill"
                  marginRight="10px"
                  maxW="23rem"
                  onClick={unMarkAsInvoiced}
                  isDisabled={isLoading}>
                  Unmark As Invoiced
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
      }
      <Flex my={8} gap={8} width="100%">
        <Flex color="black" flexDir="row" flexGrow={1}>
          <Flex mr={5} w="100%">
            <FormikTextArea
              name="invoiceNotes"
              label="Invoice Notes"
              h="100%"
              isReadOnly
              description={editInCustomerDetailsLink}
              value={formikValues.invoiceNotes || invoiceNotes}
            />
          </Flex>
          <FormikTextArea
            name="billingDetails"
            label="Billing Details"
            h="100%"
            isReadOnly
            description={editInCustomerDetailsLink}
            value={report?.site?.billingDetails || 'N/A'}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
export default InvoicingTab;
